import { pathEq } from 'ramda';

import { runOnce } from '@utils/funcs.ts';
import { StateObjMultiple } from '@utils/stateObj.ts';

import { widgetEnum } from '../../../../utils/constants.ts';
import { POI_VIEW_ID_DESKTOP, POI_VIEW_ID_MOBILE } from '../src/constants.ts';
import PoiViewWidget from './PoiViewWidget.tsx';

async function create(app, config, initState) {
  const widgetState = initState || new StateObjMultiple();

  const init = async () => {
    app.bus.get('venueData/getVenueCategory').then(venueCategory => widgetState.update({ venueCategory }));
    app.bus.get('venueData/getVenueTimezone').then(venueTimezone => widgetState.update({ venueTimezone }));

    const dlp = config.deepLinkProps;

    if (dlp) {
      app.bus.on(
        'map/mapReadyToShow',
        runOnce(async () => {
          if (dlp?.poiId) {
            return showPoi(dlp.poiId);
          }
        }),
      );
    }

    const venueId = await app.bus.get('venueData/getVenueId');
    widgetState.update({ venueId });
    if (config.hidePoiFeedback) {
      widgetState.update({ hidePoiFeedback: config.hidePoiFeedback });
    }

    // Manually registering this plugin
    app.bus.send('ariaAnnouncer/registerNamespace', {
      announcementKey: 'poiFeedback',
    });
  };

  const initialState = {
    poi: null,
    layout: [],
    searchInputsLength: 0,
    // cleared per POI so a previous POI's status never leaks into the next one
    openStatus: undefined,
  };

  widgetState.update(initialState);

  const shouldSuppressPOIView = () => app.env.isMobile() && app.config.uiHide && app.config.uiHide.mobileHidePOIView;

  const handlers = {
    onGetDirectionsClicked: () => {
      // only keep existing stops if we are adding stops, not if we are initiating a new directions session
      const keepExistingStops = widgetState.getState().searchInputsLength > 1;
      app.bus.send('poiDetails/navigateToPoi', { keepExistingStops });
    },
    onCloseClicked: closePoiView,
    onTagClicked: tag => {
      app.bus.send('layers/hideMultiple', [POI_VIEW_ID_DESKTOP, POI_VIEW_ID_MOBILE]);
      releaseOverlayPadding();
      app.bus.send('searchResults/searchForTag', { tag });
    },
    onSecurityLanePoiClick: poiId =>
      app.bus.send('poiDetails/showPoi', {
        poi: poiId,
        info: { referrer: 'entityView' },
      }),
    submitPoiFeedback: (accurate, poi, venueId) => {
      // Send an announcement for screen readers
      app.bus.send('ariaAnnouncer/announce', {
        announcementKey: 'poiFeedback',
        announcementText: app.gt()('poiView: Thank you for your feedback!'),
      });
      app.bus.send('appInsights/log', {
        name: `poiView/accuracyClick`,
        properties: {
          accurate,
          poiAccuratePoiId: poi.poiId,
          poiAccurateVenueId: venueId,
          poiAccurateCategory: poi.category,
        },
      });
    },
  };

  app.bus.send('layers/register', {
    id: POI_VIEW_ID_DESKTOP,
    widget: () => PoiViewWidget({ app, handlers, widgetState }),
    layoutId: 'content',
    layoutName: 'fullscreen',
    widgetType: widgetEnum.Desktop,
  });

  app.bus.send('layers/register', {
    id: POI_VIEW_ID_MOBILE,
    widget: () => PoiViewWidget({ app, handlers, widgetState }),
    layoutId: 'bottomBar',
    layoutName: 'fullscreen',
    widgetType: widgetEnum.Mobile,
  });

  app.bus.on('mapLevelSelector/selectBuilding', async () => {
    if (!(await isPoiViewVisible())) {
      return;
    }
    if (widgetState.getState().poi) {
      closePoiView();
    }
  });

  app.bus.on('mapLevelSelector/selectLevel', async ({ id }) => {
    if (!(await isPoiViewVisible())) {
      return;
    }
    if (!pathEq(id, ['poi', 'position', 'floorId'], widgetState.getState())) {
      closePoiView();
    }
  });

  async function isPoiViewVisible() {
    return (
      app.bus.send('layers/isShowing', { id: POI_VIEW_ID_DESKTOP }).then(res => res.length > 0 && res[0]) ||
      app.bus.send('layers/isShowing', { id: POI_VIEW_ID_MOBILE }).then(res => res.length > 0 && res[0])
    );
  }

  // True while the map carries bottom padding for the mobile POI detail (see 'expandableContent/reportHeight')
  let overlayPaddingApplied = false;

  /**
   * Restores padding to no longer include the detail overlay.
   */
  const releaseOverlayPadding = async () => {
    if (!overlayPaddingApplied) {
      return;
    }
    overlayPaddingApplied = false;
    const padding = await app.bus.getFirst('map/getPadding');
    if (padding) {
      app.bus.send('map/changePadding', { padding, keepView: true });
    }
  };

  function closePoiView() {
    app.bus.send('history/stepBack');
    app.bus.send('layers/hideMultiple', [POI_VIEW_ID_DESKTOP, POI_VIEW_ID_MOBILE]);
    releaseOverlayPadding();
    app.bus.send('map/cleanMap');
    widgetState.update({ ...initialState });
  }

  const notifyState = () => {
    const { poi } = widgetState.getState();
    const sendState = {
      id: 'online/poiView',
    };
    if (poi && poi?.poiId) {
      sendState.poiId = poi.poiId;
    }
    app.bus.send('deepLinking/notifyState', sendState);
  };
  widgetState.addCallback(notifyState);

  app.bus.on('deepLinking/setState', stateOb => {
    if (stateOb.id === 'online/poiView') {
      if (stateOb.poiId) {
        showPoi(stateOb.poiId);
      } else {
        widgetState.update({ poi: null });
      }
    }
  });

  const showPoi = async (poi, info = {}) => {
    widgetState.update({ ...initialState, shouldCenterPoiOnHeightReport: app.env.isMobile() }); // reset previous poi data, ensure POI will be animated to on mobile env
    const poiId = typeof poi !== 'object' ? poi : poi.poiId;

    poi = await app.bus.get('poi/getById', { id: poiId });

    // Be defensive here, as this can be triggered with a URL param which can be invalid
    if (!poi) {
      console.warn(`Call to showPoi with invalid POI ID: ${poiId}`);
      return;
    }

    const searchInputsLength = await app.bus.get('navigation/getSearchInputsLength');

    if (app.debug) {
      window._poi = poi;
    }

    poi = await app.bus.get('poi/addOtherSecurityLanes', { poi });

    const structures = await app.bus.get('venueData/getStructures');

    const layoutName = Object.keys(config.layouts).find(
      key => poi.category === key || poi.category.startsWith(key + '.'),
    );
    const layout = layoutName ? config.layouts[layoutName] : config.layouts.default;
    widgetState.update({ poi, layout, structures, searchInputsLength });

    updateOpenStatus(poi.poiId);

    app.bus.send('bluedot/stopFollowing');

    app.bus.send('map/selectEntities', { ids: [poi.poiId] });

    if (!shouldSuppressPOIView()) {
      app.bus.send('layers/show', { id: POI_VIEW_ID_DESKTOP });
      app.bus.send('layers/show', { id: POI_VIEW_ID_MOBILE });
      app.bus.send('layers/hideWithLayoutId', 'headerOnline');
      if (!app.env.isDesktop()) {
        app.bus.send('layers/hideWithLayoutId', 'content');
      }
      app.bus.send('history/register', {
        viewId: app.env.isMobile() ? POI_VIEW_ID_MOBILE : POI_VIEW_ID_DESKTOP,
        event: 'poiDetails/showPoi',
        params: { poi, info },
      });
    }

    // If app is not mobile, animate to POI, else wait for POI view to report its height
    if (!app.env.isMobile() || shouldSuppressPOIView()) {
      const { latitude, longitude, floorId } = poi.position;
      app.bus.send('map/animateToPointWithRadius', {
        lat: latitude,
        lng: longitude,
        floorId,
        radius: poi.zoomRadius,
        relSpeed: 1.0,
      });
    }
  };

  app.bus.on('poiDetails/showPoi', ({ poi, info }) => showPoi(poi, info));

  app.bus.on('poiDetails/getActivePoiId', () => widgetState.getState().poi?.poiId || null);

  app.bus.monitor('poi/setDynamicData', async ({ idValuesMap }) => {
    const { poi } = widgetState.getState();
    if (poi && Object.hasOwnProperty.call(idValuesMap, poi.poiId)) {
      const updatedPoi = await app.bus.get('poi/getById', { id: poi.poiId });
      // showPoi(updatedPoi)
      widgetState.update({ poi: updatedPoi });
      // Dynamic data can override open/closed status, so re-resolve it alongside the POI
      updateOpenStatus(poi.poiId);
    }
  });

  /**
   * Resolves the POI's open/closed status and publishes it on widget state.
   *
   * Resolved here rather than stored on the POI because the answer depends on the current
   * time. Re-evaluated each time a POI is opened and whenever its dynamic data changes.
   *
   */
  const updateOpenStatus = async poiId => {
    // getFirst resolves to undefined when poiDataManager is not loaded, while still letting a
    // genuine rejection from the listener surface rather than swallowing it.
    const openStatus = await app.bus.getFirst('poi/getOpenStatus', { id: poiId });

    // Guard against a slow response landing after the user moved to a different POI
    const { poi: currentPoi } = widgetState.getState();
    if (currentPoi && currentPoi.poiId === poiId) {
      widgetState.update({ openStatus });
    }
  };

  app.bus.on('poiDetails/navigateToPoi', ({ keepExistingStops }) => {
    app.bus.send('layers/hideMultiple', [POI_VIEW_ID_DESKTOP, POI_VIEW_ID_MOBILE]);
    releaseOverlayPadding();
    app.bus.send('navigation/searchDirectionsToId', {
      poiId: widgetState.getState().poi.poiId,
      referrer: 'entityView',
      keepExistingStops,
    });
  });

  app.bus.on('map/poiClicked', async ({ poi }) => {
    const isNavigationSearchView = await app.bus
      .send('layers/isShowing', { id: 'DirectionsSearchControls' })
      .then(res => res.length > 0 && res[0]);
    if (!isNavigationSearchView) {
      app.bus.send('poiDetails/showPoi', { poi, info: { referrer: 'map' } });
    }
  });

  app.bus.on('expandableContent/reportHeight', async ({ height, visibleHeight }) => {
    if (widgetState.getState().shouldCenterPoiOnHeightReport) {
      const poi = widgetState.getState().poi;
      const { latitude, longitude, floorId } = poi.position;
      widgetState.update({ shouldCenterPoiOnHeightReport: false }); // ensure padding gets added only once - when new POI is displayed

      // On mobile the POI view is a overlay that floats OVER the bottom of the map (the map keeps its full
      // height), so the POI must be centered in the part of the map the overlay leaves visible. Keep the
      // app-level padding on the other sides and pad the bottom by the overlay's collapsed height.
      const overlayHeight = visibleHeight ?? height ?? 0;
      const appPadding = (await app.bus.getFirst('map/getPadding')) ?? {};

      overlayPaddingApplied = true;
      app.bus.send('map/animateToPointWithRadius', {
        lat: latitude,
        lng: longitude,
        floorId,
        radius: poi.zoomRadius,
        padding: { top: 0, left: 0, right: 0, ...appPadding, bottom: (appPadding.bottom ?? 0) + overlayHeight },
      });
    }
  });

  return {
    init,
    widgetState: () => widgetState,
    internal: {
      handlers,
      notifyState,
      isPoiViewVisible,
      closePoiView,
      showPoi,
    },
  };
}

export { create };
